/**
 * De-para: o texto da OS vira estação.
 *
 * A OS diz "Equip./Fornecedor: SM74". O sistema precisa saber que isso é a
 * impressora do setor de Impressão, e que "Click Konica Minolta C284" não é
 * tarefa de ninguém — é custo por clique.
 *
 * **Texto que ninguém ensinou não impede a OS de entrar** (RN-005). A etapa
 * nasce "a classificar" e aparece numa fila de triagem; a OS segue para a
 * carteira. Barrar a importação por causa de um equipamento novo pararia a
 * produção inteira por causa de uma palavra.
 */

import type { DeParaEstacao, Secao, TipoLinha } from '@pcp/dominio';

export interface Classificacao {
  estacaoId: string | null;
  tipoLinha: TipoLinha;
  aClassificar: boolean;
  /** A regra que casou, para a tela poder explicar de onde veio. */
  regraId: string | null;
}

export interface ChaveDeEtapa {
  secao: Secao;
  equipamentoOs: string | null;
  nomeEtapa: string;
}

/**
 * Casa do mais específico para o mais genérico. Uma regra para
 * ("acabamento", "Guilhotina", null) vale para toda etapa que rode na
 * guilhotina, e uma para ("acabamento", "Guilhotina", "Refile") ganha dela.
 */
export function classificar(
  chave: ChaveDeEtapa,
  regras: readonly DeParaEstacao[],
): Classificacao {
  const eq = normalizar(chave.equipamentoOs);
  const nome = normalizar(chave.nomeEtapa);

  const candidatas = regras
    .map((r) => ({ regra: r, peso: pesoDaRegra(r, chave.secao, eq, nome) }))
    .filter((c) => c.peso > 0)
    .sort((a, b) => b.peso - a.peso);

  const melhor = candidatas[0];
  if (!melhor) {
    return { estacaoId: null, tipoLinha: 'operacao', aClassificar: true, regraId: null };
  }

  const r = melhor.regra;
  return {
    estacaoId: r.estacaoId,
    tipoLinha: r.tipoLinha,
    // Uma regra de custo ou de ignorar não precisa de estação; uma de operação,
    // sim — sem estação a etapa não tem fila onde aparecer.
    aClassificar: r.tipoLinha === 'operacao' && r.estacaoId === null,
    regraId: r.id,
  };
}

/** Zero = não casa. Quanto maior, mais específica. */
function pesoDaRegra(
  regra: DeParaEstacao,
  secao: Secao,
  equipamento: string | null,
  nome: string | null,
): number {
  if (regra.secao !== null && regra.secao !== secao) return 0;

  const regraEq = normalizar(regra.equipamentoOs);
  const regraNome = normalizar(regra.nomeEtapa);

  if (regraEq !== null && regraEq !== equipamento) return 0;
  if (regraNome !== null && regraNome !== nome) return 0;

  let peso = 1;
  if (regra.secao !== null) peso += 1;
  if (regraEq !== null) peso += 4;
  if (regraNome !== null) peso += 8;
  return peso;
}

/**
 * Compara sem diferença de caixa nem de acento.
 *
 * A OS escreve "intercalação" na coluna do equipamento e "Intercalar Blocos"
 * no nome, e a mesma máquina aparece como "GTO" e "GTO52". Exigir igualdade
 * exata faria o encarregado classificar a mesma coisa três vezes.
 */
export function normalizar(texto: string | null | undefined): string | null {
  if (texto === null || texto === undefined) return null;
  const t = texto
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
  return t === '' ? null : t;
}
